import type {
  CreateReportSchedule,
  CreateSavedFilter,
  ReplaceReportSchedule,
  ReportKey,
  ReportSchedule,
  ReportScheduleRun,
  SavedFilter,
  SaveReportSettings,
} from "@station/contracts";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { api } from "../../lib/api.js";
import { queryKeys } from "../../lib/query.js";
import { useFeatures, usePermissions } from "../../lib/session.js";
import type { DateRange } from "./report-values.js";

/**
 * Raporlama ekranlarının sorguları (Prompt 22 web). Sunucu her bölümü kaynağın izniyle süzer (izinsiz bölüm `null`); burada
 * yalnızca taban izinler aranır: `reports.read` (+ `REPORTS` bayrağı) panolar ve rapor merkezi, `reports.schedule.manage`
 * zamanlanmış raporlar, `reports.settings.manage` rapor ayarları (logo). Kaydedilmiş filtre / favori kişiseldir.
 */
export interface ReportPermissions {
  enabled: boolean;
  canRead: boolean;
  canSchedule: boolean;
  canManageSettings: boolean;
  /** Kârlılık (ana defter) — `accounting.entry.read` + `ACCOUNTING`. */
  canSeeProfitability: boolean;
  canSeeFuel: boolean;
  canSeeFinance: boolean;
}

export function useReportPermissions(): ReportPermissions {
  const permissions = usePermissions();
  const features = useFeatures();
  const enabled = features.has("REPORTS");
  const canRead = enabled && permissions.has("reports.read");
  return {
    enabled,
    canRead,
    canSchedule: enabled && permissions.has("reports.schedule.manage"),
    canManageSettings: enabled && permissions.has("reports.settings.manage"),
    canSeeProfitability: canRead && permissions.has("accounting.entry.read") && features.has("ACCOUNTING"),
    canSeeFuel: canRead && permissions.has("fuel.ops.read") && features.has("FUEL"),
    canSeeFinance: canRead && permissions.hasAny("treasury.read", "cheques.read", "counterparty.report.read"),
  };
}

export function useRefreshReports(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.reports() });
  }, [queryClient]);
}

const MINUTE = 60_000;

// ── Panolar ───────────────────────────────────────────────────────────────────
export function useReportsHome(enabled = true) {
  return useQuery({
    queryKey: queryKeys.reports("home"),
    enabled,
    refetchInterval: MINUTE,
    queryFn: async () => (await api.GET("/reports/home")).data!,
  });
}

export function useLiveToday(enabled = true) {
  return useQuery({
    queryKey: queryKeys.reports("live-today"),
    enabled,
    refetchInterval: MINUTE,
    queryFn: async () => (await api.GET("/reports/live-today")).data!,
  });
}

export interface ScopeFilter {
  companyId: string | null;
  stationIds: string[];
}

export function useStationComparison(range: DateRange, scope: ScopeFilter, enabled = true) {
  const query = {
    from: range.from,
    to: range.to,
    ...(scope.companyId ? { companyId: scope.companyId } : {}),
    ...(scope.stationIds.length > 0 ? { stationIds: scope.stationIds.join(",") } : {}),
  };
  return useQuery({
    queryKey: queryKeys.reports("comparison", query),
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/reports/station-comparison", { params: { query } })).data!,
  });
}

export function useFuelAnalytics(
  range: DateRange,
  filter: { companyId: string | null; stationId: string | null; windowDays: number },
  enabled = true,
) {
  const query = {
    from: range.from,
    to: range.to,
    windowDays: filter.windowDays,
    ...(filter.companyId ? { companyId: filter.companyId } : {}),
    ...(filter.stationId ? { stationId: filter.stationId } : {}),
  };
  return useQuery({
    queryKey: queryKeys.reports("fuel", query),
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/reports/fuel-analytics", { params: { query } })).data!,
  });
}

export function useFinanceOverview(companyId: string | null, enabled = true) {
  const query = companyId ? { companyId } : {};
  return useQuery({
    queryKey: queryKeys.reports("finance", query),
    enabled,
    queryFn: async () => (await api.GET("/reports/finance-overview", { params: { query } })).data!,
  });
}

export function useConsistency(range: DateRange, companyId: string | null, enabled = true) {
  const query = { from: range.from, to: range.to, companyId: companyId ?? "" };
  return useQuery({
    queryKey: queryKeys.reports("consistency", query),
    enabled: enabled && companyId !== null,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/reports/consistency", { params: { query } })).data!,
  });
}

export function useProfitability(
  range: DateRange,
  companyId: string | null,
  options: { groupBy: "STATION" | "BUSINESS_UNIT" | "STATION_BUSINESS_UNIT"; includeDrafts: boolean },
  enabled = true,
) {
  const query = {
    companyId: companyId ?? "",
    dateFrom: range.from,
    dateTo: range.to,
    groupBy: options.groupBy,
    includeDrafts: options.includeDrafts ? ("true" as const) : ("false" as const),
  };
  return useQuery({
    queryKey: queryKeys.reports("profitability", query),
    enabled: enabled && companyId !== null,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/accounting/reports/dimension-profitability", { params: { query } })).data!,
  });
}

// ── Rapor merkezi ─────────────────────────────────────────────────────────────
export function useReportCatalog(enabled = true) {
  return useQuery({
    queryKey: queryKeys.reports("catalog"),
    enabled,
    queryFn: async () => (await api.GET("/reports/catalog")).data!,
  });
}

export function useSavedFilters(reportKey: ReportKey, enabled = true) {
  return useQuery({
    queryKey: queryKeys.reports("filters", reportKey),
    enabled,
    queryFn: async () =>
      (await api.GET("/reports/saved-filters", { params: { query: { reportKey } } })).data!.items,
  });
}

export function useSavedFilterMutations(reportKey: ReportKey) {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.reports("filters", reportKey) });
  const create = useMutation({
    mutationFn: async (input: Omit<CreateSavedFilter, "reportKey">) =>
      (await api.POST("/reports/saved-filters", { body: { ...input, reportKey } })).data! as SavedFilter,
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async (input: {
      filter: SavedFilter;
      patch: { name?: string; isDefault?: boolean; params?: Record<string, unknown> };
    }) =>
      (
        await api.PATCH("/reports/saved-filters/{id}", {
          params: { path: { id: input.filter.id } },
          body: { ...input.patch, version: input.filter.version },
        })
      ).data! as SavedFilter,
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (filter: SavedFilter) => {
      await api.DELETE("/reports/saved-filters/{id}", { params: { path: { id: filter.id } } });
    },
    onSuccess: invalidate,
  });
  return { create, update, remove };
}

export function useFavoriteToggle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reportKey: ReportKey; favorite: boolean }) =>
      input.favorite
        ? (
            await api.PUT("/reports/favorites/{reportKey}", {
              params: { path: { reportKey: input.reportKey } },
            })
          ).data!
        : (
            await api.DELETE("/reports/favorites/{reportKey}", {
              params: { path: { reportKey: input.reportKey } },
            })
          ).data!,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.reports("catalog") }),
  });
}

// ── Rapor ayarları ────────────────────────────────────────────────────────────
export function useReportSettings(enabled = true) {
  return useQuery({
    queryKey: queryKeys.reports("settings"),
    enabled,
    queryFn: async () => (await api.GET("/reports/settings")).data!,
  });
}

export function useSaveReportSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveReportSettings) =>
      (await api.PUT("/reports/settings", { body: input })).data!,
    onSuccess: (settings) => queryClient.setQueryData(queryKeys.reports("settings"), settings),
  });
}

// ── Zamanlanmış raporlar ──────────────────────────────────────────────────────
export function useReportSchedules(enabled = true) {
  return useQuery({
    queryKey: queryKeys.reports("schedules"),
    enabled,
    queryFn: async () => (await api.GET("/reports/schedules")).data!.items,
  });
}

export function useScheduleRuns(scheduleId: string | null) {
  return useQuery({
    queryKey: queryKeys.reports("schedule-runs", scheduleId),
    enabled: scheduleId !== null,
    queryFn: async () =>
      (await api.GET("/reports/schedules/{id}/runs", { params: { path: { id: scheduleId! } } })).data!.items,
  });
}

export function useRecipientCandidates(enabled = true) {
  return useQuery({
    queryKey: queryKeys.reports("recipients"),
    enabled,
    staleTime: 5 * MINUTE,
    queryFn: async () => (await api.GET("/reports/schedule-recipients")).data!.items,
  });
}

export function useScheduleMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.reports("schedules") });
  const create = useMutation({
    mutationFn: async (input: CreateReportSchedule) =>
      (await api.POST("/reports/schedules", { body: input })).data! as ReportSchedule,
    onSuccess: invalidate,
  });
  const replace = useMutation({
    mutationFn: async (input: { id: string; body: ReplaceReportSchedule }) =>
      (await api.PUT("/reports/schedules/{id}", { params: { path: { id: input.id } }, body: input.body }))
        .data! as ReportSchedule,
    onSuccess: invalidate,
  });
  const action = useMutation({
    mutationFn: async (input: { schedule: ReportSchedule; action: "pause" | "resume" | "archive" }) => {
      const options = {
        params: { path: { id: input.schedule.id } },
        body: { version: input.schedule.version },
      };
      const response =
        input.action === "pause"
          ? await api.POST("/reports/schedules/{id}/pause", options)
          : input.action === "resume"
            ? await api.POST("/reports/schedules/{id}/resume", options)
            : await api.POST("/reports/schedules/{id}/archive", options);
      return response.data! as ReportSchedule;
    },
    onSuccess: invalidate,
  });
  const runNow = useMutation({
    mutationFn: async (schedule: ReportSchedule) =>
      (await api.POST("/reports/schedules/{id}/run", { params: { path: { id: schedule.id } } }))
        .data! as ReportScheduleRun,
    onSuccess: async (_run, schedule) => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: queryKeys.reports("schedule-runs", schedule.id) });
    },
  });
  return { create, replace, action, runNow };
}
